import {
  Divider,
  Modal,
  ScrollArea,
  Select,
  TextInput,
  Textarea,
} from '@mantine/core';
import {DatePickerInput} from '@mantine/dates';
import {useForm} from '@mantine/form';
import {type FC, Fragment} from 'react';
import {MdAccessTime} from 'react-icons/md';
import {COLOR_BLACK_2} from '../../../../../config/constants/colorCodes';
import {ENTRY_MANAGEMENTS_TODO} from '../../../../../config/constants/routes';
import {PRIORITY_OPTIONS} from '../../../../../core/entities/entryTodos/constants';
import {cn} from '../../../../../lib/classname';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {Z_INDEX} from '../../../constants/zIndex';
import {formatDateForClient} from '../../../usecase/formatDateForClient';
import type {
  TodoCandidateType,
  TodoCandidateWithId,
} from '../../../usecase/useAiTodoGeneration';
import {InsideModalButton} from './InsideModalButton';
import {ModalCancelButton} from './ModalCancelButton';
import {NotesText} from './Progress/NotesText';
import {PriorityTag} from './Todo/PriorityTag';

// 編集フォーム用の型（Date型を使用）
type TodoCandidateFormValues = {
  title: string;
  priority: string | null; // Selectコンポーネントは文字列を返す
  dueDate: Date | null;
  body: string;
  link?: string;
};

// 編集フォームコンポーネント
type TodoCandidateEditFormProps = {
  candidate: TodoCandidateType;
  onUpdate: (updated: TodoCandidateType) => void;
  onCancel: () => void;
};

// データ型変換ユーティリティ
const convertToFormValues = (
  candidate: TodoCandidateType,
): TodoCandidateFormValues => ({
  title: candidate.title,
  priority: candidate.priority ? String(candidate.priority) : null,
  dueDate: candidate.dueDate ? new Date(candidate.dueDate) : null,
  body: candidate.body || '',
  link: candidate.link,
});

const convertFromFormValues = (
  values: TodoCandidateFormValues,
): TodoCandidateType => ({
  title: values.title,
  priority: values.priority ? Number(values.priority) : null,
  dueDate: values.dueDate ? values.dueDate.toISOString() : null,
  body: values.body,
  link: values.link,
});

const TodoCandidateEditForm: FC<TodoCandidateEditFormProps> = ({
  candidate,
  onUpdate,
  onCancel,
}) => {
  const form = useForm<TodoCandidateFormValues>({
    initialValues: convertToFormValues(candidate),
    validate: {
      title: (value) => (value.trim() ? null : 'タイトルは必須です'),
      link: (value) => {
        if (!value || value.trim() === '') return null;
        try {
          new URL(value);
          return null;
        } catch {
          return '有効なURLを入力してください';
        }
      },
    },
  });

  const handleSubmit = (values: TodoCandidateFormValues) => {
    onUpdate(convertFromFormValues(values));
  };

  return (
    <form
      onSubmit={form.onSubmit(handleSubmit)}
      className="flex flex-col gap-12"
    >
      <TextInput
        label="タイトル"
        placeholder="ToDoのタイトルを入力"
        required
        {...form.getInputProps('title')}
      />
      <div className="flex justify-between gap-16">
        <DatePickerInput
          label="締切"
          valueFormat="YYYY/MM/DD"
          popoverProps={{withinPortal: true}}
          clearable
          value={form.values.dueDate}
          onChange={(value) => form.setFieldValue('dueDate', value)}
          error={form.errors.dueDate}
        />
        <Select
          label="優先度"
          placeholder="選択してください"
          data={PRIORITY_OPTIONS}
          {...form.getInputProps('priority')}
        />
      </div>

      <Textarea
        label="詳細メモ"
        placeholder="詳細な説明やメモを入力"
        minRows={3}
        maxRows={6}
        {...form.getInputProps('body')}
      />

      <div className="flex gap-8 justify-end">
        <InsideModalButton onClick={onCancel} filled={false}>
          キャンセル
        </InsideModalButton>
        <InsideModalButton type="submit" filled={true}>
          確定
        </InsideModalButton>
      </div>
    </form>
  );
};

type Props = {
  opened: boolean;
  onClose: () => void;
  candidates: TodoCandidateWithId[];
  onSave: (candidate: TodoCandidateWithId, index: number) => Promise<boolean>;
  onSaveAll: () => Promise<void>;
  isTodoSaved: (index: number) => boolean;
  isSaving: boolean;
  isSavingAll: boolean;
  savingIndex: number | null;
  editingIndex: number | null;
  editingCandidate: TodoCandidateWithId | null;
  onStartEdit: (index: number) => void;
  onCancelEdit: () => void;
  onUpdateCandidate: (index: number, updated: TodoCandidateWithId) => void;
};

export const AiGeneratedTodosModal: FC<Props> = ({
  opened,
  onClose,
  candidates,
  onSave,
  onSaveAll,
  isTodoSaved,
  isSaving,
  isSavingAll,
  savingIndex,
  editingIndex,
  editingCandidate,
  onStartEdit,
  onCancelEdit,
  onUpdateCandidate,
}) => {
  if (candidates.length === 0) return null;

  // 未保存のToDo数を計算
  const unsavedCount = candidates.filter(
    (_, index) => !isTodoSaved(index),
  ).length;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="AI ToDo候補"
      radius={16}
      centered
      size={'lg'}
      zIndex={Z_INDEX.MODAL}
      styles={{
        title: {
          fontWeight: 'bold',
        },
        content: {
          // SP時にモーダルの高さを確保
          maxHeight: '95vh',
        },
      }}
    >
      <div className="flex flex-col gap-16">
        <div className="flex flex-col gap-16">
          <div className="text-sm text-black-1">
            保存したいToDoの保存ボタンを押してください。
          </div>

          {unsavedCount > 0 && (
            <div className="flex items-center justify-between gap-16">
              <div className="text-sm text-black-2" aria-live="polite">
                未保存: {unsavedCount}件
              </div>
              <InsideModalButton
                onClick={onSaveAll}
                loading={isSavingAll}
                disabled={isSavingAll || isSaving}
                filled={true}
              >
                全て保存
              </InsideModalButton>
            </div>
          )}
        </div>

        {/* ToDo候補リストのスクロールエリア（SP/PC両方で高さを固定しフッターを常に表示） */}
        <ScrollArea type="always" className="px-16 h-[50vh] pc:h-[450px]">
          <div className="flex flex-col gap-12">
            {candidates.map((candidate, index) => {
              const saved = isTodoSaved(index);
              const saving = savingIndex === index;
              const hasDueDate = candidate.dueDate !== null;
              const isEditing = editingIndex === index;

              return (
                <Fragment key={candidate.candidateId}>
                  <div
                    className={cn(
                      'relative flex flex-col gap-12',
                      isEditing && 'p-12',
                    )}
                  >
                    {!isEditing ? (
                      <>
                        <div className="flex flex-col items-center gap-16">
                          <div className="flex-1 flex flex-col gap-8">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-8">
                                <div className="text-base text-black-1 font-bold">
                                  {candidate.title}
                                </div>
                                {candidate.priority && (
                                  <PriorityTag level={candidate.priority} />
                                )}
                              </div>
                            </div>

                            {hasDueDate && (
                              <div className="flex gap-4 items-center text-black-2 text-sm">
                                <MdAccessTime fill={COLOR_BLACK_2} />
                                <span>
                                  {formatDateForClient(candidate.dueDate)} 締切
                                </span>
                              </div>
                            )}

                            {candidate.body && (
                              <div className="text-sm bg-background-2 rounded-16 p-12">
                                <NotesText>{candidate.body}</NotesText>
                              </div>
                            )}
                          </div>

                          <div className="w-full flex items-center justify-end gap-8">
                            <InsideModalButton
                              onClick={() => onStartEdit(index)}
                              disabled={saved || isSaving || isSavingAll}
                              aria-label={`${candidate.title}を編集`}
                            >
                              編集
                            </InsideModalButton>
                            <InsideModalButton
                              onClick={() => onSave(candidate, index)}
                              loading={saving}
                              disabled={
                                saved ||
                                isSavingAll ||
                                (isSaving && savingIndex !== index)
                              }
                              filled={true}
                              aria-label={`${candidate.title}を${saved ? '保存済み' : '保存'}`}
                            >
                              {saved ? '保存済み' : '保存'}
                            </InsideModalButton>
                          </div>
                        </div>
                      </>
                    ) : (
                      <TodoCandidateEditForm
                        candidate={editingCandidate || candidate}
                        onUpdate={(updated) =>
                          onUpdateCandidate(index, {
                            ...updated,
                            candidateId: candidate.candidateId,
                          })
                        }
                        onCancel={onCancelEdit}
                      />
                    )}
                  </div>
                  {index < candidates.length - 1 && <Divider my="sm" />}
                </Fragment>
              );
            })}
          </div>
        </ScrollArea>

        <div className="flex flex-col items-center justify-center gap-8">
          <ModalCancelButton onClick={onClose}>閉じる</ModalCancelButton>
          <CommonLink
            href={ENTRY_MANAGEMENTS_TODO}
            className="text-link-1 text-base underline"
          >
            ToDo一覧ページへ
          </CommonLink>
        </div>
      </div>
    </Modal>
  );
};
